import { getUserFacingError } from "./error-presentation.js";

const PROVIDER_PREFERENCE_KEY = "sharemyai.wallet-provider.v1";

export function createWalletDiscovery(browser = globalThis) {
  const announced = new Map();

  const announce = (event) => {
    const provider = event?.detail?.provider;
    const info = event?.detail?.info;
    if (!provider?.request || !info?.name) return;
    announced.set(provider, normalizeProvider(provider, info));
  };

  browser.addEventListener?.("eip6963:announceProvider", announce);

  return {
    request() {
      browser.dispatchEvent?.(new Event("eip6963:requestProvider"));
    },
    list() {
      const providers = [...announced.values()];
      const legacy = browser.ethereum;
      const legacyProviders = Array.isArray(legacy?.providers) ? legacy.providers : [legacy];
      for (const candidate of legacyProviders) {
        if (candidate?.request && !providers.some((entry) => entry.provider === candidate)) {
          providers.push(normalizeProvider(candidate, legacyProviderInfo(candidate)));
        }
      }
      return providers;
    },
    destroy() {
      browser.removeEventListener?.("eip6963:announceProvider", announce);
    }
  };
}

export function selectPreferredProvider(providers, storedKey) {
  if (!Array.isArray(providers) || providers.length === 0) return null;
  return providers.find((entry) => entry.key === storedKey) || providers[0];
}

export function loadProviderPreference(storage = globalThis.localStorage) {
  try { return storage?.getItem(PROVIDER_PREFERENCE_KEY) || null; }
  catch { return null; }
}

export function saveProviderPreference(key, storage = globalThis.localStorage) {
  try {
    if (key) storage?.setItem(PROVIDER_PREFERENCE_KEY, key);
    else storage?.removeItem(PROVIDER_PREFERENCE_KEY);
  } catch {}
}

export function friendlyWalletError(error) {
  const code = Number(error?.code);
  const message = String(error?.message || "Wallet request failed.");
  if (code === 4001) return "Request cancelled. Reopen your wallet and approve each step to continue.";
  if (code === -32002) return "A wallet request is already open. Reopen the wallet extension to finish it.";
  if (/returned no account|no account/i.test(message)) {
    return "The wallet returned no account. Unlock it, select an account, and try again.";
  }
  if (/unsupported|not supported/i.test(message) && /sign|typed/i.test(message)) {
    return "This wallet does not support the required EIP-712 sign-in. Try a current EVM wallet extension.";
  }
  return getUserFacingError(error, message);
}

function normalizeProvider(provider, info) {
  const name = String(info.name || "Browser wallet");
  const rdns = String(info.rdns || "");
  const uuid = String(info.uuid || "");
  return {
    provider,
    info: { name, rdns, uuid },
    key: rdns || uuid || name.toLowerCase().replace(/\s+/g, "-")
  };
}

function legacyProviderInfo(provider) {
  const name = provider.isRabby ? "Rabby Wallet"
    : provider.isBraveWallet ? "Brave Wallet"
      : provider.isCoinbaseWallet ? "Coinbase Wallet"
        : provider.isMetaMask ? "MetaMask"
          : "Browser wallet";
  return { name, rdns: `legacy.${name.toLowerCase().replace(/\s+/g, "-")}`, uuid: "" };
}
